import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import OnHumanHRLogo from "./logos/OnHumanHRLogo";
import OnHumanTechLogo from "../tech/logos/OnHumanTechLogo";

// Two-logo brand switch. Each side is a brand wordmark and a clickable link.
// The container styling adapts to whichever site you're currently on (`active`),
// so the switch feels native on both HR and Tech.
// `onTechNavigate` / `onHRNavigate` (optional): intercept the click on that side
// so the caller can run a transition before navigating. Absent = plain link.
// Sizing is responsive here rather than at the call site, so the switch lands in
// the same place at the same size on both domains and the logo doesn't jump
// across the brand transition.
export default function BrandSwitch({ active = "hr", onTechNavigate, onHRNavigate }) {
  const isHR = active === "hr";

  const sideCls = (isActiveSide, ring) =>
    `relative z-10 min-w-0 flex items-center justify-center px-3 sm:px-5 py-1.5 sm:py-2 rounded-lg sm:rounded-xl outline-none focus-visible:ring-2 ${ring} transition-all duration-500 ${
      isActiveSide
        ? "opacity-100"
        : "opacity-40 grayscale hover:opacity-75 hover:grayscale-0"
    }`;

  return (
    <div
      className="relative grid grid-cols-2 items-stretch rounded-xl sm:rounded-2xl p-1 sm:p-1.5 select-none bg-transparent"
      role="group"
      aria-label="OnHuman brand switch"
    >
      {/* Sliding gradient highlight */}
      <motion.div
        aria-hidden
        className="absolute top-1 bottom-1 left-1 sm:top-1.5 sm:bottom-1.5 sm:left-1.5 rounded-lg sm:rounded-xl pointer-events-none w-[calc(50%-4px)] sm:w-[calc(50%-6px)]"
        initial={false}
        animate={{ x: isHR ? "0%" : "100%" }}
        transition={{ type: "spring", stiffness: 280, damping: 30 }}
      >
        <motion.div
          className="w-full h-full rounded-lg sm:rounded-xl backdrop-blur-md"
          initial={false}
          animate={{
            background: isHR
              ? "rgba(241,245,249,0.95)"
              : "rgba(5,8,20,0.92)",
            boxShadow: isHR
              ? "0 0 38px -4px rgba(59,130,246,0.7), inset 0 0 0 1px rgba(59,130,246,0.45)"
              : "0 0 38px -4px rgba(34,211,238,0.7), inset 0 0 0 1px rgba(168,85,247,0.45)",
          }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        />
      </motion.div>

      {/* HR side */}
      <Link
        to="/anamenu"
        onClick={
          onHRNavigate
            ? (e) => {
                e.preventDefault();
                onHRNavigate();
              }
            : undefined
        }
        aria-pressed={isHR}
        aria-label="OnHuman HR"
        title="OnHuman"
        className={sideCls(isHR, "focus-visible:ring-blue-300/60")}
      >
        <OnHumanHRLogo className="h-6 sm:h-8 w-auto max-w-full" />
      </Link>

      {/* Tech side */}
      <Link
        to="/onhumantech"
        onClick={
          onTechNavigate
            ? (e) => {
                e.preventDefault();
                onTechNavigate();
              }
            : undefined
        }
        aria-pressed={!isHR}
        aria-label="OnHuman Technology"
        title="OnHuman Technology"
        className={sideCls(!isHR, "focus-visible:ring-cyan-300/60")}
      >
        <OnHumanTechLogo className="h-6 sm:h-8 w-auto max-w-full" />
      </Link>
    </div>
  );
}
